import React, { useEffect } from 'react';
import T from 'prop-types';
import classnames from 'classnames';
import Button from './button';

// We forward the suggestions ref from parent field component
// https://reactjs.org/docs/forwarding-refs.html

const Suggestions = React.forwardRef(function Suggestions(props, ref) {
  const {
    items,
    isDefault,
    defaultItems,
    selected,
    isOpen,
    close,
    useCloseButton,
    footer,
    handleClick,
    hightlightTitle,
    highlightSelection,
    defaultTitle,
    title,
    className,
  } = props;

  function handleKeyboard(e) {
    // Here we handle keyboard input.
    // Should only trigger when suggestionfield is visible.
    // If we need to handle Enter click - we do that here.

    if (isDefault) {
      return;
    }

    const buttons = Array.prototype.slice.call(
      ref.current.querySelectorAll('[data-element=suggestion]')
    );

    // If the current element is one of the buttons.
    const selectedIndex = buttons.findIndex(
      (btn) => btn === document.activeElement
    );

    let newIndex = selectedIndex;

    // up
    if (e.keyCode === 38) {
      e.preventDefault();
      newIndex = selectedIndex - 1;
    }

    // down
    if (e.keyCode === 40) {
      e.preventDefault();
      newIndex = selectedIndex + 1;
    }

    // enter
    if (e.keyCode === 13) {
      if (
        document &&
        document.activeElement &&
        document.activeElement.tagName === 'INPUT'
      ) {
        e.preventDefault();
      }
    }

    if (buttons[newIndex]) {
      buttons[newIndex].focus();
    }
  }

  // We have a selected option.
  const selectedText = [...items, ...defaultItems].find(
    (item) => item.text === selected
  );

  const isHighlight = Boolean(highlightSelection && selectedText);

  const isVisible = Boolean(isOpen && (items.length > 0 || isHighlight));

  // Classname for the wrapper
  const classNames = classnames(className, {
    [`${className}--is-open`]: isVisible,
  });

  // We do not want focus when the list is hidden
  const btnTabIndex = {
    tabIndex: isVisible ? 0 : -1,
  };

  // When a selections is made and we hightligt that.
  // We also display the default/fallback items below.
  const listItems = isHighlight ? defaultItems : items;

  useEffect(() => {
    if (isVisible) {
      window.addEventListener('keydown', handleKeyboard);
    } else {
      window.removeEventListener('keydown', handleKeyboard);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyboard);
    };
  }, [handleKeyboard, isVisible]);

  return (
    <div
      tabIndex="-1"
      ref={ref}
      id={`container_${props.id}`}
      className={classNames}
    >
      {useCloseButton && (
        <Button
          className={`${className}__close`}
          {...btnTabIndex}
          onClick={close}
          text="Stäng"
        />
      )}
      {title && <span className={`${className}__title`}>{title}</span>}

      <ul>
        {isHighlight && (
          <>
            <li className="highlight">
              <div>
                <p>
                  {hightlightTitle}:
                  <br />
                  <b>{selectedText.text}</b>
                </p>
              </div>
            </li>
            {defaultTitle && (
              <li className={`${className}__title`}>{defaultTitle}</li>
            )}
          </>
        )}
        {listItems.map((item, i) => (
          <li
            className={`${className}__item ${
              item.className ? item.className : ''
            }`}
            key={`${props.id}_${item.key}_${i}`}
          >
            <Button
              data-element={isDefault || isHighlight ? 'default' : 'suggestion'}
              type="button"
              tabIndex={isDefault && isVisible ? 0 : -1}
              text={item.text}
              onClick={handleClick({ ...item, value: item.value || item.text })}
            />
          </li>
        ))}
      </ul>
      {footer && footer(isVisible)}
      <div
        className="show-for-sr"
        id={props.id}
        role="status"
        aria-atomic="true"
        aria-live="assertive"
      >
        {isVisible && !isDefault && items.length > 0 && (
          <span>{items.length} förslag</span>
        )}
      </div>
    </div>
  );
});

Suggestions.defaultProps = {
  footer: null,
  className: 'suggestions',
  defaultItems: [],
  items: [],
};

Suggestions.propTypes = {
  id: T.string.isRequired,
  hightlightTitle: T.string,
  items: T.array,
  defaultItems: T.array,
  isOpen: T.bool,
  handleClick: T.func,
  close: T.func,
  /** render prop with argument `isVisibile` */
  footer: T.func,
  highlightSelection: T.bool,
  selected: T.string,
  useCloseButton: T.bool,
  title: T.string,
};

export default Suggestions;
